import React, { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { projectStateType } from 'types';
import { projectTypes, issuanceTypes } from 'types/projectType';
import { calculatePercentage } from 'util/percent';
import { formatNumber } from 'util/formatNumber';
import { calculateAmountStocks } from 'util/calculateAmountStocks';
import { useMediaQuery, useCategorizations } from 'hooks';
import {
  GenericProjectCard,
  statusProjectsFinanced,
} from './GenericProjectCard';
import { CampaignProjectCard } from './CampaignProjectCard';

export const Project = ({
  project, color, from, useCategorizationColors, isDefaultCategorization,
}) => {
  const { categorizations, defaultCategorization } = useCategorizations();
  const categorization = useMemo(() => {
    if (categorizations && project) {
      return categorizations?.find((v) => v?.id === project?.categorizationId);
    }
    return undefined;
  }, [categorizations]);

  const details = () => {
    if (project?.projectType === projectTypes.EQUITY) {
      return [
        {
          name: 'Retorno estimado',
          value: `${project?.ieaTax * 100}% E.A. aproximado. `,
        },
        {
          name: 'Inversionistas',
          value: project?.currentInvestorsAmount,
        },
      ];
    }
    if (project?.projectType === projectTypes.STOCKS) {
      return [
        {
          name: `${
            project.issuanceType === issuanceTypes.INDIVIDUAL
              ? 'Valor por la Acción'
              : 'Valor del Paquete'
          }`,
          value: formatNumber(calculateAmountStocks(project)),
        },
        {
          name: `${
            project.issuanceType === issuanceTypes.INDIVIDUAL
              ? 'Cantidad de Acciones'
              : 'Acciones por Paquete'
          }`,
          value: `${
            project.issuanceType === issuanceTypes.INDIVIDUAL
              ? project.numberNodes
              : project.nodePerPackage
          }`,
        },
      ];
    }
    if (project?.projectType === projectTypes.TOKENS) {
      return [
        {
          name: `${
            project.issuanceType === issuanceTypes.INDIVIDUAL
              ? 'Valor por Token'
              : 'Valor del Paquete'
          }`,
          value: formatNumber(calculateAmountStocks(project)),
        },
        {
          name: `${
            project.issuanceType === issuanceTypes.INDIVIDUAL
              ? 'Cantidad de Tokens'
              : 'Tokens por Paquete'
          }`,
          value: `${
            project.issuanceType === issuanceTypes.INDIVIDUAL
              ? project.numberNodes
              : project.nodePerPackage
          }`,
        },
      ];
    }
    if (statusProjectsFinanced.includes(project.status)) {
      return [
        {
          name: 'Interés',
          value: `${calculatePercentage(project?.ieaTax)}% E.A.`,
        },
        {
          name: 'Inversionistas',
          value: project.currentInvestorsAmount,
        },
      ];
    }

    return [
      {
        name: 'Interés',
        value: `${calculatePercentage(project?.ieaTax)}% E.A.`,
      },
      {
        name: 'Garantía',
        value: `${
          project?.isWarranty
            ? ` ${calculatePercentage(project?.warranty)}%`
            : 'Sin garantía'
        }`,
        size: !project?.isWarranty && 12,
      },
      {
        name: 'Tiempo de financiación',
        value: `${project?.returnTime} ${
          project.returnTime === 1 ? 'mes' : 'meses'
        }`,
      },
    ];
  };

  if (project.status === projectStateType.ADVERTISING_CAMPAIGN) {
    return (
      <CampaignProjectCard
        from={from}
        {...project}
        color={color}
        details={details()}
      />
    );
  }

  return (
    <GenericProjectCard
      from={from}
      isDefaultCategorization={isDefaultCategorization}
      {...project}
      details={details()}
      color={useCategorizationColors
        ? categorization !== defaultCategorization && categorization?.color : color}
    />
  );
};

export const LoadingProject = () => {
  const isDesktop = useMediaQuery(1023);
  const isTablet = useMediaQuery(760);
  return (
    <Skeleton
      height="490px"
      width="280px"
      style={{
        borderRadius: '25px',
        margin: (isDesktop || isTablet) && '0 30px 0 0',
      }}
    />
  );
};
